import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  VStack,
  Text,
  Badge,
  Box,
  Flex,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { ShieldCheck } from "lucide-react";
import { AdminRole } from "../../../types/admin";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: AdminRole | null;
}

export function RolePermissionsDrawer({ isOpen, onClose, role }: Props) {
  const titleColor  = useColorModeValue("#434347", "#e8edf2");
  const descColor   = useColorModeValue("#696a71", "#8fa3b8");
  const subtleColor = useColorModeValue("#84858c", "#718096");
  const headerBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const itemBg      = useColorModeValue("white", "#0f2040");
  const itemBorder  = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const iconBg      = useColorModeValue("rgba(0,158,251,0.10)", "rgba(0,158,251,0.15)");

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px" borderColor={headerBorder} pb={4}>
          <Flex align="center" gap={3} pr={8}>
            <Flex
              w="36px" h="36px"
              borderRadius="8px"
              bg={iconBg}
              align="center"
              justify="center"
              flexShrink={0}
            >
              <Icon as={ShieldCheck} boxSize={4} color="#009efb" />
            </Flex>
            <Box>
              <Text fontSize="md" fontWeight={600} color={titleColor} lineHeight="1.2">
                {role?.name}
              </Text>
              <Text fontSize="xs" color={subtleColor}>Permissões do papel</Text>
            </Box>
          </Flex>
        </DrawerHeader>

        <DrawerBody pt={5}>
          <VStack align="stretch" spacing={4}>
            {role?.description && (
              <Text fontSize="sm" color={descColor}>{role.description}</Text>
            )}

            <Box>
              <Text fontWeight={600} fontSize="sm" color={titleColor} mb={3}>
                Códigos de Acesso Vinculados
              </Text>
              {!role?.permissions.length ? (
                <Text color={subtleColor} fontSize="sm" fontStyle="italic">
                  Nenhuma permissão vinculada a este papel.
                </Text>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {role.permissions.map((perm) => (
                    <Box
                      key={perm.id}
                      p={3}
                      bg={itemBg}
                      borderRadius="8px"
                      borderWidth="1px"
                      borderColor={itemBorder}
                      boxShadow="0px 1px 4px rgba(14, 61, 93, 0.06)"
                      _hover={{ borderColor: "rgba(0,158,251,0.25)" }}
                      transition="border-color 0.15s"
                    >
                      <Badge
                        borderRadius="50px"
                        bg="rgba(0,158,251,0.12)"
                        color="#009efb"
                        fontFamily="mono"
                        fontSize="11px"
                        fontWeight={700}
                        letterSpacing="0.5px"
                        px={2.5}
                        py={0.5}
                        mb={1.5}
                        display="inline-block"
                      >
                        {perm.code}
                      </Badge>
                      <Text fontSize="xs" color={subtleColor}>
                        {perm.description || "Sem descrição"}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
